import {
  pathMatchIgnoreLocale,
  removePathPrefix,
} from '@/ui/utils/localization-helpers/localization';
import { ItemRelationsFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';

/**
 * Maps Crystallize Include/Exclude page relations into the de-localized frontend paths they
 * represent. `removePathPrefix` strips the `webhierarchy` catalogue root so the result lines up
 * with what `pathMatchIgnoreLocale` produces from `usePathname()`.
 */
function getRelationPaths(relation?: ItemRelationsFragment): string[] {
  return (relation?.items ?? [])
    .map((item) => item?.path)
    .filter((path): path is string => Boolean(path))
    .map((path) => removePathPrefix(path).replace(/\/$/, ''));
}

function matchesAnyRelation(currentPath: string, relation?: ItemRelationsFragment): boolean {
  return getRelationPaths(relation).some((matchPath) =>
    pathMatchIgnoreLocale(currentPath, matchPath));
}

/**
 * Decides whether a page-targeted element should render on the current page based on CMS
 * Include/Exclude page relations.
 *
 * - When Include pages are configured, the element shows ONLY on those pages (Include always takes
 *   precedence and Exclude is ignored).
 * - Otherwise, the element shows everywhere except the Exclude pages.
 */
export function shouldRenderOnPage(
  currentPath: string,
  includePages?: ItemRelationsFragment,
  excludePages?: ItemRelationsFragment,
): boolean {
  const includePaths = getRelationPaths(includePages);

  if (includePaths.length > 0) {
    return includePaths.some((matchPath) => pathMatchIgnoreLocale(currentPath, matchPath));
  }

  return !matchesAnyRelation(currentPath, excludePages);
}
